import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Plus, Trash2 } from "lucide-react";

const EMPTY_ITEM = { description: "", hsn_sac: "", quantity: 1, rate: 0 };
const EMPTY = {
  vendor_name: "", vendor_gstin: "", invoice_number: "", bill_date: "", due_date: "", status: "Unpaid",
  gst_type: "None", cgst_rate: "", sgst_rate: "", igst_rate: "", notes: "",
};

export default function PurchaseInvoiceFormDialog({ open, onOpenChange, invoice, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [items, setItems] = useState([{ ...EMPTY_ITEM }]);
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const isEdit = !!invoice;

  useEffect(() => {
    if (open) {
      if (invoice) {
        setForm({
          vendor_name: invoice.vendor_name, vendor_gstin: invoice.vendor_gstin || "", invoice_number: invoice.invoice_number,
          bill_date: invoice.bill_date, due_date: invoice.due_date || "", status: invoice.status,
          gst_type: invoice.gst_type || "None", cgst_rate: invoice.cgst_rate || "", sgst_rate: invoice.sgst_rate || "",
          igst_rate: invoice.igst_rate || "", notes: invoice.notes || "",
        });
        setItems(invoice.line_items?.length ? invoice.line_items.map((it) => ({ description: it.description, hsn_sac: it.hsn_sac || "", quantity: it.quantity, rate: it.rate })) : [{ ...EMPTY_ITEM }]);
      } else {
        setForm(EMPTY);
        setItems([{ ...EMPTY_ITEM }]);
      }
      setFile(null);
    }
  }, [open, invoice]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const updateItem = (idx, key, value) => setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, [key]: value } : it)));
  const addItem = () => setItems((prev) => [...prev, { ...EMPTY_ITEM }]);
  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));

  const taxable = items.reduce((sum, it) => sum + (Number(it.quantity) || 0) * (Number(it.rate) || 0), 0);
  const cgstAmt = form.gst_type === "CGST+SGST" ? (taxable * (Number(form.cgst_rate) || 0)) / 100 : 0;
  const sgstAmt = form.gst_type === "CGST+SGST" ? (taxable * (Number(form.sgst_rate) || 0)) / 100 : 0;
  const igstAmt = form.gst_type === "IGST" ? (taxable * (Number(form.igst_rate) || 0)) / 100 : 0;
  const total = taxable + cgstAmt + sgstAmt + igstAmt;

  const handleSubmit = async () => {
    const validItems = items.filter((it) => it.description.trim());
    if (!form.vendor_name.trim() || !form.invoice_number.trim() || !form.bill_date || validItems.length === 0) {
      toast.error("Vendor, invoice number, bill date, and at least one line item are required");
      return;
    }
    setSubmitting(true);
    const payload = {
      vendor_name: form.vendor_name.trim(), vendor_gstin: form.vendor_gstin || null, invoice_number: form.invoice_number.trim(),
      bill_date: form.bill_date, due_date: form.due_date || null, status: form.status,
      line_items: validItems.map((it) => ({ description: it.description, hsn_sac: it.hsn_sac || null, quantity: Number(it.quantity) || 1, rate: Number(it.rate) || 0 })),
      gst_type: form.gst_type, cgst_rate: Number(form.cgst_rate) || 0, sgst_rate: Number(form.sgst_rate) || 0, igst_rate: Number(form.igst_rate) || 0,
      notes: form.notes || null,
    };
    try {
      let savedId = invoice?.id;
      if (isEdit) {
        await api.put(`/purchase-invoices/${invoice.id}`, payload);
        toast.success("Purchase invoice updated");
      } else {
        const res = await api.post("/purchase-invoices", payload);
        savedId = res.data.id;
        toast.success("Purchase invoice recorded");
      }
      if (file && savedId) {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/purchase-invoices/${savedId}/file`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="purchase-invoice-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{isEdit ? "Edit Purchase Invoice" : "New Purchase Invoice"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Vendor Name *</Label>
              <Input data-testid="purchase-invoice-vendor-input" value={form.vendor_name} onChange={set("vendor_name")} placeholder="e.g. ABC Suppliers Pvt Ltd" />
            </div>
            <div className="space-y-1.5">
              <Label>Vendor GSTIN</Label>
              <Input data-testid="purchase-invoice-vendor-gstin-input" value={form.vendor_gstin} onChange={set("vendor_gstin")} placeholder="27AAAAA0000A1Z5" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label>Invoice Number *</Label>
              <Input data-testid="purchase-invoice-number-input" value={form.invoice_number} onChange={set("invoice_number")} placeholder="VEN-2026-001" />
            </div>
            <div className="space-y-1.5">
              <Label>Bill Date *</Label>
              <Input data-testid="purchase-invoice-bill-date-input" type="date" value={form.bill_date} onChange={set("bill_date")} />
            </div>
            <div className="space-y-1.5">
              <Label>Due Date</Label>
              <Input data-testid="purchase-invoice-due-date-input" type="date" value={form.due_date} onChange={set("due_date")} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger data-testid="purchase-invoice-status-select" className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Unpaid">Unpaid</SelectItem>
                <SelectItem value="Paid">Paid</SelectItem>
                <SelectItem value="Overdue">Overdue</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Line Items</Label>
              <Button type="button" size="sm" variant="outline" onClick={addItem} data-testid="purchase-invoice-add-item-button">
                <Plus className="h-3.5 w-3.5 mr-1" /> Add Item
              </Button>
            </div>
            <div className="space-y-2" data-testid="purchase-invoice-line-items">
              {items.map((it, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-center" data-testid={`purchase-invoice-line-item-${idx}`}>
                  <Input className="col-span-5" data-testid={`purchase-invoice-item-description-${idx}`} value={it.description} onChange={(e) => updateItem(idx, "description", e.target.value)} placeholder="Description" />
                  <Input className="col-span-2" data-testid={`purchase-invoice-item-hsn-${idx}`} value={it.hsn_sac} onChange={(e) => updateItem(idx, "hsn_sac", e.target.value)} placeholder="HSN/SAC" />
                  <Input className="col-span-2" data-testid={`purchase-invoice-item-qty-${idx}`} type="number" min="0" value={it.quantity} onChange={(e) => updateItem(idx, "quantity", e.target.value)} placeholder="Qty" />
                  <Input className="col-span-2" data-testid={`purchase-invoice-item-rate-${idx}`} type="number" min="0" value={it.rate} onChange={(e) => updateItem(idx, "rate", e.target.value)} placeholder="Rate" />
                  <button type="button" data-testid={`purchase-invoice-item-remove-${idx}`} onClick={() => removeItem(idx)} disabled={items.length === 1} className="col-span-1 text-red-600 disabled:opacity-30">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-4 gap-4">
            <div className="space-y-1.5">
              <Label>GST Type</Label>
              <Select value={form.gst_type} onValueChange={(v) => setForm({ ...form, gst_type: v })}>
                <SelectTrigger data-testid="purchase-invoice-gst-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="None">None</SelectItem>
                  <SelectItem value="CGST+SGST">CGST + SGST</SelectItem>
                  <SelectItem value="IGST">IGST</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.gst_type === "CGST+SGST" && (
              <>
                <div className="space-y-1.5">
                  <Label>CGST %</Label>
                  <Input data-testid="purchase-invoice-cgst-rate-input" type="number" min="0" value={form.cgst_rate} onChange={set("cgst_rate")} placeholder="9" />
                </div>
                <div className="space-y-1.5">
                  <Label>SGST %</Label>
                  <Input data-testid="purchase-invoice-sgst-rate-input" type="number" min="0" value={form.sgst_rate} onChange={set("sgst_rate")} placeholder="9" />
                </div>
              </>
            )}
            {form.gst_type === "IGST" && (
              <div className="space-y-1.5">
                <Label>IGST %</Label>
                <Input data-testid="purchase-invoice-igst-rate-input" type="number" min="0" value={form.igst_rate} onChange={set("igst_rate")} placeholder="18" />
              </div>
            )}
          </div>

          <div className="bg-[#F8F9FA] border border-[#E2E8F0] rounded-md p-3 text-sm space-y-1" data-testid="purchase-invoice-gst-summary">
            <div className="flex justify-between"><span className="text-[#4B5563]">Taxable Amount</span><span className="font-medium">₹{taxable.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span></div>
            {form.gst_type === "CGST+SGST" && (
              <>
                <div className="flex justify-between"><span className="text-[#4B5563]">CGST</span><span>₹{cgstAmt.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span></div>
                <div className="flex justify-between"><span className="text-[#4B5563]">SGST</span><span>₹{sgstAmt.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span></div>
              </>
            )}
            {form.gst_type === "IGST" && (
              <div className="flex justify-between"><span className="text-[#4B5563]">IGST</span><span>₹{igstAmt.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span></div>
            )}
            <div className="flex justify-between font-semibold text-[#1C1C1E] border-t border-[#E2E8F0] pt-1 mt-1">
              <span>Total</span><span data-testid="purchase-invoice-total-preview">₹{total.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Notes</Label>
            <Textarea data-testid="purchase-invoice-notes-input" value={form.notes} onChange={set("notes")} rows={2} />
          </div>
          <div className="space-y-1.5">
            <Label>{isEdit && invoice?.original_filename ? `Bill File (current: ${invoice.original_filename})` : "Vendor Bill/Invoice File"}</Label>
            <Input data-testid="purchase-invoice-file-input" type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="purchase-invoice-form-cancel-button">Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="purchase-invoice-form-submit-button" className="bg-[#76C121] hover:bg-[#5C9819] text-white">
            {submitting ? "Saving..." : isEdit ? "Save Changes" : "Record Invoice"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
